import { useState, useEffect } from 'react'
import useTranslate from '../../../i18n/useTranslate'
import _ from 'lodash'
import { makeStyles } from '@material-ui/core'
import { ClosedDataListType } from '../../../services/um/useSearchClosed'
import { convertDateToFormatTH, convertDateToFormatEN } from '../../../utilities/date'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import CustomPagination from '../closed/CustomPagination'
import CustomNoRowsOverlay from '../closed/CustomNoRowsOverlay'

type DataRowsType = {
    id: number
    projectNumber: string
    receiveDate: string
    closedDate: string
    name: string
    position: string
    projectType: string
    projectNameTH: string
    projectNameEN: string
}

type ClosePanelComponentProps = {
    dataList: ClosedDataListType[]
}

const Table = ({ dataList }: ClosePanelComponentProps) => {
    const classes = useStyles()
    const translate = useTranslate()
    const lang = translate.languageStore.currentLanguage as 'th' | 'en'
    const [rows, setRows] = useState<DataRowsType[]>([])

    useEffect(() => {
        convertData()
    }, [dataList, lang])

    const convertData = () => {
        if (dataList.length > 0) {
            let rows = dataList.map((item: ClosedDataListType) => {
                return {
                    id: item.projectID,
                    projectNumber: item.projectNumber,
                    receiveDate: convertDate(item.receiveDate),
                    closedDate: convertDate(item.closedDate),
                    name: item.name?.[lang] ?? '-',
                    position: item.researcherTypeName?.[lang] ?? '-',
                    projectNameTH: item.projectName?.th ?? '-',
                    projectNameEN: item.projectName?.en ?? '-',
                    projectType: item.projectType.name[lang] ?? '-',
                }
            })
            setRows(rows)
        } else {
            setRows([])
        }
    }

    const convertDate = (date: string | null) => {
        if (lang === 'th' && date) {
            return convertDateToFormatTH(date)
        } else if (lang === 'en' && date) {
            return convertDateToFormatEN(date)
        } else {
            return '-'
        }
    }

    const column: GridColDef[] = [
        {
            field: 'projectNumber',
            headerName: `${translate.get('UM.CLOSED.DATA_TABLE.COLUMN1')}`,
            width: 180,
            headerClassName: 'header-table',
            headerAlign: 'left',
            sortable: true,
        },
        {
            field: 'name',
            headerName: `${translate.get('UM.CLOSED.DATA_TABLE.COLUMN4')}`,
            width: 300,
            headerClassName: 'header-table',
            headerAlign: 'left',
            sortable: true,
        },
        {
            field: 'projectType',
            headerName: `${translate.get('UM.CLOSED.DATA_TABLE.COLUMN6')}`,
            width: 200,
            headerClassName: 'header-table',
            headerAlign: 'left',
            sortable: true,
        },
        {
            field: 'projectNameTH',
            headerName: `${translate.get('UM.CLOSED.DATA_TABLE.COLUMN7')}`,
            width: 400,
            headerClassName: 'header-table',
            headerAlign: 'left',
            sortable: true,
        },
        {
            field: 'projectNameEN',
            headerName: `${translate.get('UM.CLOSED.DATA_TABLE.COLUMN8')}`,
            width: 400,
            headerClassName: 'header-table',
            headerAlign: 'left',
            sortable: true,
        },
    ]

    return (
        <div style={{ width: '100%', marginTop: 16 }}>
            <DataGrid
                className={classes.root}
                rows={rows}
                columns={column}
                autoHeight={true}
                pagination
                pageSize={10}
                rowsPerPageOptions={[10]}
                sortingOrder={['asc', 'desc']}
                disableColumnMenu={true}
                disableSelectionOnClick={true}
                components={{
                    Pagination: CustomPagination,
                    NoRowsOverlay: CustomNoRowsOverlay,
                }}
            />
        </div>
    )
}

const useStyles = makeStyles((theme) => ({
    root: {
        '& .super-app-theme--fffff': {
            backgroundColor: '#DB5F8E',
            '&:hover': {
                backgroundColor: '#DB5F8E',
            },
        },
        '& .super-app-theme--header': {
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
        },
        '& .MuiDataGrid-columnSeparator': {
            visibility: 'hidden',
        },
        '& .MuiDataGrid-row': {
            lineHeigh: '70px',
        },

        '& .header-table': {
            backgroundColor: '#DB5F8E',
            color: '#FFFFFF',
        },
        '& .MuiDataGrid-iconSeparator': {
            display: 'none',
        },
        '& .MuiDataGrid-sortIcon': {
            color: '#FFFFFF',
        },
        '& .MuiBadge-anchorOriginTopRightRectangle': {
            display: 'none',
        },
        '& .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },

        width: '100%',
        marginTop: 16,

        overflowX: 'hidden',
    },
}))

export default Table
